import React, { useEffect, useRef, useState } from 'react';
import api from '../lib/api';
import { openSummaryPanel, logUiEvent } from '../lib/events';
import { STATUS_COLORS, istDay } from '../lib/format';
import { Q_STATUS_COLORS, inr2 } from '../lib/quotations';
import { MEETING_STATUS_COLORS } from '../lib/meetings';
import { Badge } from './ui/badge';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Search, User, Building2, FileText, CalendarDays } from 'lucide-react';
import logger from '../lib/logger';

const EMPTY_RESULTS = { leads: [], staff: [], quotations: [], meetings: [] };

/** Ctrl/Cmd+K toggles the search dialog.
 * Registered only by the primary (bar) instance — the mobile icon instance is
 * mounted simultaneously, and double-registration would open two stacked dialogs. */
const useSearchHotkey = (setOpen, enabled) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setOpen, enabled]);
};

const normalizeResults = (data) => ({
  leads: data.leads || [],
  staff: data.staff || [],
  quotations: data.quotations || [],
  meetings: data.meetings || [],
});

/** Fetch + guard against stale responses; seq ref invalidates slow requests. */
const performSearch = async (query, seq, setResults, setLoading) => {
  const mySeq = (seq.current += 1);
  setLoading(true);
  try {
    const { data } = await api.get('/search', { params: { q: query } });
    if (mySeq !== seq.current) return; // stale response — a newer query is in flight
    setResults(normalizeResults(data));
  } catch (e) {
    if (mySeq !== seq.current) return;
    logger.warn('Global search failed', e);
    logUiEvent('SEARCH_ERROR', { type: 'search', details: `query="${query}" — ${e?.message || 'request failed'}` });
    setResults(EMPTY_RESULTS);
  } finally {
    if (mySeq === seq.current) setLoading(false);
  }
};

/** Debounced backend search across leads / staff / meetings / quotations.
 * A request sequence guard makes sure an older (slow) response can never
 * overwrite the results of a newer query. */
const useDebouncedSearch = (query, open) => {
  const [results, setResults] = useState(EMPTY_RESULTS);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const seq = useRef(0);

  useEffect(() => {
    if (!open) return undefined;
    if (timer.current) clearTimeout(timer.current);
    if (query.trim().length < 2) {
      seq.current += 1; // invalidate any in-flight request
      setResults(EMPTY_RESULTS);
      setLoading(false);
      return undefined;
    }
    timer.current = setTimeout(() => performSearch(query.trim(), seq, setResults, setLoading), 250);
    return () => clearTimeout(timer.current);
  }, [query, open]);

  return { results, loading };
};

const searchEmptyMessage = (query, loading) => {
  if (query.trim().length < 2) return 'Type at least 2 characters to search';
  if (loading) return 'Searching…';
  return 'No results found';
};

const SearchTrigger = ({ variant, onOpen }) =>
  variant === 'bar' ? (
    <button
      onClick={onOpen}
      data-testid="global-search-trigger"
      className="flex items-center gap-2 h-9 w-full max-w-[340px] rounded-lg border bg-muted/50 px-3 text-sm text-muted-foreground hover:bg-muted transition-colors"
    >
      <Search size={14} />
      <span className="flex-1 text-left truncate">Search leads, meetings, staff…</span>
      <kbd className="hidden md:inline-flex h-5 items-center rounded border bg-background px-1.5 text-[10px] font-medium">Ctrl K</kbd>
    </button>
  ) : (
    <button
      onClick={onOpen}
      data-testid="global-search-trigger-mobile"
      aria-label="Search"
      className="h-9 w-9 rounded-lg flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
    >
      <Search size={18} />
    </button>
  );

// Row subtitles precomputed outside JSX (review: no filter/join in render expressions)
const leadSubtitle = (l) => [l.phone || 'no phone', l.setter_name || l.closer_name, l.team].filter(Boolean).join(' · ');
const staffSubtitle = (s) => [s.role, (s.teams || []).join(', '), s.email].filter(Boolean).join(' · ');

const LeadResults = ({ leads, onSelect }) => {
  if (leads.length === 0) return null;
  return (
    <CommandGroup heading="Leads">
      {leads.map((l) => (
        <CommandItem key={l.id} value={`lead-${l.id}`} onSelect={() => onSelect('lead', l.id, l.lead_name)} data-testid={`search-lead-${l.id}`}>
          <Building2 size={14} className="mr-2 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{l.lead_name}{l.company_name ? ` · ${l.company_name}` : ''}</div>
            <div className="text-xs text-muted-foreground truncate">{leadSubtitle(l)}</div>
          </div>
          <Badge variant="outline" className={`ml-2 text-[10px] ${STATUS_COLORS[l.status] || ''}`}>{l.status}</Badge>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

const QuotationResults = ({ quotations, onSelect }) => {
  if (quotations.length === 0) return null;
  return (
    <CommandGroup heading="Quotations">
      {quotations.map((q) => (
        <CommandItem key={q.id} value={`quotation-${q.id}`} onSelect={() => onSelect('quotation', q.id, q.number)} data-testid={`search-quotation-${q.id}`}>
          <FileText size={14} className="mr-2 text-blue-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{q.number}{q.client?.name ? ` · ${q.client.name}` : ''}</div>
            <div className="text-xs text-muted-foreground truncate capitalize">{q.doc_type} · {inr2(q.totals?.grand_total)}</div>
          </div>
          <Badge variant="outline" className={`ml-2 text-[10px] ${Q_STATUS_COLORS[q.status] || ''}`}>{q.status}</Badge>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

const MeetingResults = ({ meetings, onSelect }) => {
  if (meetings.length === 0) return null;
  return (
    <CommandGroup heading="Meetings">
      {meetings.map((m) => (
        <CommandItem key={m.id} value={`meeting-${m.id}`} onSelect={() => onSelect('meeting', m.id, m.title)} data-testid={`search-meeting-${m.id}`}>
          <CalendarDays size={14} className="mr-2 text-emerald-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{m.title}</div>
            <div className="text-xs text-muted-foreground truncate">
              {m.lead_name}{m.start_at ? ` · ${istDay(m.start_at).format('DD-MM-YYYY h:mm A')}` : ''}{m.mode ? ` · ${m.mode}` : ''}
            </div>
          </div>
          <Badge variant="outline" className={`ml-2 text-[10px] ${MEETING_STATUS_COLORS[m.status] || ''}`}>{m.status}</Badge>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

const StaffResults = ({ staff, onSelect }) => {
  if (staff.length === 0) return null;
  return (
    <CommandGroup heading="Staff">
      {staff.map((s) => (
        <CommandItem key={s.id} value={`staff-${s.id}`} onSelect={() => onSelect('staff', s.id, s.name)} data-testid={`search-staff-${s.id}`}>
          <User size={14} className="mr-2 text-violet-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">{s.name}</div>
            <div className="text-xs text-muted-foreground truncate">{staffSubtitle(s)}</div>
          </div>
          <Badge variant="outline" className={`ml-2 text-[10px] ${s.status === 'Active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-100 text-slate-500 border-slate-200'}`}>
            {s.status || 'Staff'}
          </Badge>
        </CommandItem>
      ))}
    </CommandGroup>
  );
};

export const GlobalSearch = ({ variant = 'bar' }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  useSearchHotkey(setOpen, variant === 'bar');
  const { results, loading } = useDebouncedSearch(query, open);

  // Every result type opens its editable side panel OVER the current page —
  // no navigation, no blank routes. Clicks are recorded in the audit trail.
  // The panel open is deferred until the command dialog fully closes: opening a
  // second Radix modal during the first one's exit animation leaves a stuck
  // overlay that blocks all pointer events.
  const openPanel = (type, id, name = '') => {
    logUiEvent('SEARCH_RESULT_CLICKED', { type, id, name, details: `query="${query.trim()}"` });
    setOpen(false);
    setQuery('');
    window.setTimeout(() => openSummaryPanel(type, id), 220);
  };

  return (
    <>
      <SearchTrigger variant={variant} onOpen={() => setOpen(true)} />
      <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false} data-testid="global-search-dialog">
        <CommandInput placeholder="Search leads, meetings, quotations or staff…" value={query} onValueChange={setQuery} data-testid="global-search-input" />
        <CommandList>
          <CommandEmpty data-testid="global-search-empty">
            {searchEmptyMessage(query, loading)}
          </CommandEmpty>
          <LeadResults leads={results.leads} onSelect={openPanel} />
          <MeetingResults meetings={results.meetings} onSelect={openPanel} />
          <QuotationResults quotations={results.quotations} onSelect={openPanel} />
          <StaffResults staff={results.staff} onSelect={openPanel} />
        </CommandList>
      </CommandDialog>
    </>
  );
};
